import React, { createContext, useContext, useState, useEffect } from 'react'
import { useUser } from './UserContext'
import { FAVORITES_KEY, readScoped, writeScoped } from '../utils/userScopedStorage'

const FavoritesContext = createContext()

export const useFavorites = () => {
  const context = useContext(FavoritesContext)
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider')
  }
  return context
}

function loadFavorites(uid) {
  const saved = readScoped(FAVORITES_KEY, uid)
  return Array.isArray(saved) ? saved : []
}

export const FavoritesProvider = ({ children }) => {
  const { user } = useUser()
  const uid = user?.uid || null
  // Owner travels with the list so a save never lands under the next account's key.
  const [state, setState] = useState(() => ({ owner: uid, items: loadFavorites(uid) }))

  useEffect(() => {
    setState((prev) => (prev.owner === uid ? prev : { owner: uid, items: loadFavorites(uid) }))
  }, [uid])

  const favorites = state.owner === uid ? state.items : []

  useEffect(() => {
    writeScoped(FAVORITES_KEY, state.owner, state.items)
  }, [state])

  const toggleFavorite = (item) => {
    setState(prev => {
      const current = prev.owner === uid ? prev.items : loadFavorites(uid)
      const exists = current.find(fav => fav.id === item.id)
      const items = exists
        ? current.filter(fav => fav.id !== item.id)
        : [...current, item]
      return { owner: uid, items }
    })
  }

  const isFavorite = (itemId) => {
    return favorites.some(fav => fav.id === itemId)
  }

  const clearFavorites = () => {
    setState({ owner: uid, items: [] })
  }

  const value = {
    favorites,
    toggleFavorite,
    isFavorite,
    clearFavorites
  }

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  )
}
